import { h } from '@/lib/dom';
import { deltaDirection, flagToStatus, formatDate, formatDelta, formatLabValue, statusLabel } from '@/lib/format';
import { MarkerBadge, type MarkerStatus } from './MarkerBadge';

export interface SparklineRowProps {
  canonicalKey: string;
  name: string;
  value: number | null;
  unit: string;
  delta: number | null;
  flag: string | null;
  status?: MarkerStatus;
  sparkline: Array<number | null>;
  drawnAt?: string | null;
  onNavigate?: (key: string) => void;
}

function arrow(dir: 'up' | 'down' | 'flat' | null): string {
  if (dir === 'up') return '↑';
  if (dir === 'down') return '↓';
  if (dir === 'flat') return '—';
  return '';
}

export function SparklineRow(props: SparklineRowProps): HTMLElement {
  const status: MarkerStatus = props.status ?? flagToStatus(props.flag);
  const dir = deltaDirection(props.delta);
  const ariaLabelParts = [
    props.name,
    props.value != null ? `${formatLabValue(props.value)} ${props.unit}` : '',
    status !== 'norm' ? statusLabel(status) : '',
    props.delta != null ? `изменение ${formatDelta(props.delta, formatLabValue)}` : '',
  ].filter(Boolean);

  // Render a placeholder instead of the actual sparkline SVG.
  // IntersectionObserver in labs-overview will replace it with the real SVG.
  const sparkPlaceholder = h('span', {
    class: 'spark-placeholder',
    'aria-hidden': 'true',
    style: 'display:inline-block; width:100px; height:18px;',
  }) as HTMLElement;
  sparkPlaceholder.dataset['values'] = JSON.stringify(props.sparkline);
  sparkPlaceholder.dataset['color'] = 'var(--chart-line-primary)';

  const row = h(
    'a',
    {
      class: 'sparkline-row',
      href: `/labs/${encodeURIComponent(props.canonicalKey)}`,
      aria: { label: ariaLabelParts.join(', ') },
      role: 'link',
      on: {
        click: (ev) => {
          if (props.onNavigate) {
            ev.preventDefault();
            props.onNavigate(props.canonicalKey);
          }
        },
      },
    },
    [
      h('span', { class: 'name' }, [
        MarkerBadge({ flag: props.flag, status: props.status, ariaLabelPrefix: props.name }),
        ' ',
        h('span', null, props.name),
      ]),
      h('span', { class: 'value tabular' }, `${formatLabValue(props.value)} ${props.unit}`),
      // Дельта той же точности, что и значение слева: с formatNumber по умолчанию
      // разница микроэлементов печаталась как «+0».
      h(
        'span',
        { class: 'delta tabular' },
        props.delta != null ? `${formatDelta(props.delta, formatLabValue)} ${arrow(dir)}` : '',
      ),
      sparkPlaceholder,
      h('span', { class: 'date' }, formatDate(props.drawnAt ?? null)),
      h('span', { class: 'chevron', 'aria-hidden': 'true' }, '→'),
    ],
  );
  return row;
}
